import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Collapse,
  Tag,
  Anchor,
  Divider,
  Row,
  Col,
  Space,
  List,
  Alert,
  Steps,
  Timeline,
} from 'antd';
import {
  DashboardOutlined,
  BarChartOutlined,
  SettingOutlined,
  FileTextOutlined,
  GiftOutlined,
  RocketOutlined,
  SendOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { helpCategories, quickStartItems, HelpItem } from './helpData';


const { Title, Paragraph, Text } = Typography;


// Иконки категорий
const categoryIcons: Record<string, React.ReactNode> = {
  'Основное': <DashboardOutlined />,
  'Маркетинг': <GiftOutlined />,
  'Контент': <FileTextOutlined />,
  'Аналитика': <BarChartOutlined />,
  'Управление': <SettingOutlined />,
};


// Цвета тегов планов
const planColors: Record<string, string> = {
  starter: 'blue',
  business: 'gold',
  enterprise: 'purple',
};


const planLabels: Record<string, string> = {
  starter: 'Starter',
  business: 'Business',
  enterprise: 'Enterprise',
};


// Карточка раздела (для справочных секций)
const HelpSectionCard: React.FC<{ item: HelpItem }> = ({ item }) => (
  <Card
    id={item.id}
    style={{ marginBottom: 16 }}
    title={
      <Space>
        <Text strong style={{ fontSize: 16 }}>
          {item.title}
        </Text>
        {item.planRequired && (
          <Tag color={planColors[item.planRequired] || 'default'}>
            {planLabels[item.planRequired] || item.planRequired}
          </Tag>
        )}
        <Tag color="geekblue">{item.path}</Tag>
      </Space>
    }
  >
    <Paragraph>{item.fullDesc}</Paragraph>


    <Divider orientation="left" orientationMargin={0} style={{ fontSize: 14 }}>
      Возможности
    </Divider>
    <List
      size="small"
      dataSource={item.features}
      renderItem={(feature: string) => (
        <List.Item style={{ padding: '4px 0', borderBottom: 'none' }}>
          <Text>• {feature}</Text>
        </List.Item>
      )}
    />


    {item.tips && item.tips.length > 0 && (
      <>
        <Divider orientation="left" orientationMargin={0} style={{ fontSize: 14 }}>
          Советы
        </Divider>
        {item.tips.map((tip: string, idx: number) => (
          <Alert
            key={idx}
            message={tip}
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
        ))}
      </>
    )}
  </Card>
);


// ─── Блок «Быстрый старт» ───


const QuickStartBlock: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);


  // Данные шагов быстрого старта
  const steps = [
    {
      title: 'Настройте аккаунт',
      icon: <ShopOutlined />,
      color: '#fa8c16',
      description: 'Заполните информацию о вашем заведении',
      content: (
        <>
          <Paragraph>
            Первым делом настройте ваш аккаунт — это основа, на которой строится всё остальное.
          </Paragraph>
          <Timeline
            items={[
              {
                color: 'orange',
                children: (
                  <>
                    <Text strong>Общие настройки</Text>
                    <br />
                    <Text type="secondary">
                      Перейдите в раздел <Text code>Управление → Общие настройки</Text>. Укажите название
                      заведения, контактный телефон, email и часы работы.
                    </Text>
                  </>
                ),
              },
              {
                color: 'orange',
                children: (
                  <>
                    <Text strong>Филиал</Text>
                    <br />
                    <Text type="secondary">
                      В разделе <Text code>Управление → Филиалы</Text> укажите точный адрес точки —
                      он будет отображаться клиентам при самовывозе.
                    </Text>
                  </>
                ),
              },
              {
                color: 'orange',
                children: (
                  <>
                    <Text strong>Дизайн</Text>
                    <br />
                    <Text type="secondary">
                      В разделе <Text code>Управление → Дизайн</Text> загрузите логотип и выберите
                      фирменные цвета — они будут использоваться на сайте и в мини-приложениях.
                    </Text>
                  </>
                ),
              },
            ]}
          />
          <Alert
            message="Совет"
            description="Не забудьте указать реальный номер телефона — клиенты будут звонить по нему для уточнения заказов."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </>
      ),
    },
    {
      title: 'Создайте каталог',
      icon: <AppstoreOutlined />,
      color: '#1890ff',
      description: 'Категории, товары, модификаторы',
      content: (
        <>
          <Paragraph>
            Наполните каталог — это то, что увидят ваши клиенты на сайте и в приложениях.
          </Paragraph>
          <Timeline
            items={[
              {
                color: 'blue',
                children: (
                  <>
                    <Text strong>Категории</Text>
                    <br />
                    <Text type="secondary">
                      Перейдите в <Text code>Категории</Text> и создайте 3-5 основных категорий:
                      пицца, бургеры, роллы, напитки, десерты. Укажите порядок сортировки.
                    </Text>
                  </>
                ),
              },
              {
                color: 'blue',
                children: (
                  <>
                    <Text strong>Товары</Text>
                    <br />
                    <Text type="secondary">
                      В разделе <Text code>Товары</Text> добавьте минимум 10-15 позиций.
                      Для каждого товара загрузите фото, укажите описание, цену и привяжите к категории.
                      Если есть варианты размеров — добавьте их.
                    </Text>
                  </>
                ),
              },
              {
                color: 'blue',
                children: (
                  <>
                    <Text strong>Модификаторы (опционально)</Text>
                    <br />
                    <Text type="secondary">
                      Если клиенты могут выбирать добавки (сыр, соус, топпинги) — создайте
                      модификаторы в разделе <Text code>Модификаторы</Text> и привяжите к товарам.
                    </Text>
                  </>
                ),
              },
            ]}
          />
          <Alert
            message="Совет"
            description="Начните с минимума: 3-4 категории и 10-15 товаров. Расширить каталог можно в любой момент."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </>
      ),
    },
    {
      title: 'Доставка и оплата',
      icon: <EnvironmentOutlined />,
      color: '#52c41a',
      description: 'Зоны доставки, способы оплаты, настройки заказа',
      content: (
        <>
          <Paragraph>
            Настройте географию доставки и доступные способы оплаты.
          </Paragraph>
          <Timeline
            items={[
              {
                color: 'green',
                children: (
                  <>
                    <Text strong>Зоны доставки</Text>
                    <br />
                    <Text type="secondary">
                      В <Text code>Управление → Зоны доставки</Text> нарисуйте зоны на карте.
                      Для каждой зоны укажите стоимость доставки, минимальную сумму заказа
                      и время доставки.
                    </Text>
                  </>
                ),
              },
              {
                color: 'green',
                children: (
                  <>
                    <Text strong>Способы оплаты</Text>
                    <br />
                    <Text type="secondary">
                      В <Text code>Управление → Способы оплаты</Text> включите нужные варианты:
                      наличные при получении, оплата картой курьеру, онлайн-оплата.
                    </Text>
                  </>
                ),
              },
              {
                color: 'green',
                children: (
                  <>
                    <Text strong>Настройки заказа</Text>
                    <br />
                    <Text type="secondary">
                      В <Text code>Управление → Настройки заказа</Text> укажите минимальную сумму
                      заказа, включите/отключите самовывоз и предзаказ.
                    </Text>
                  </>
                ),
              },
            ]}
          />
        </>
      ),
    },
    {
      title: 'Подключите Telegram',
      icon: <SendOutlined />,
      color: '#0088cc',
      description: 'Создайте бота и привяжите к сервису',
      content: (
        <>
          <Paragraph>
            Telegram мини-приложение позволяет клиентам заказывать еду прямо из мессенджера —
            без установки отдельных приложений.
          </Paragraph>
          <Card
            size="small"
            style={{ background: '#f0f7ff', border: '1px solid #d6e4ff', marginBottom: 16 }}
          >
            <Text strong style={{ color: '#0088cc' }}>📱 Что получат ваши клиенты:</Text>
            <br />
            <Text type="secondary">
              Кнопка «Заказать еду» в боте → полный каталог → корзина → оформление заказа — всё внутри Telegram
            </Text>
          </Card>
          <Timeline
            items={[
              {
                color: '#0088cc',
                children: (
                  <>
                    <Text strong>1. Создайте бота</Text>
                    <br />
                    <Text type="secondary">
                      Найдите <Text code>@BotFather</Text> в Telegram → отправьте <Text code>/newbot</Text> →
                      введите имя бота (например: «Пицца Экспресс») → введите username (например: <Text code>pizza_express_bot</Text>)
                    </Text>
                  </>
                ),
              },
              {
                color: '#0088cc',
                children: (
                  <>
                    <Text strong>2. Сохраните токен</Text>
                    <br />
                    <Text type="secondary">
                      BotFather выдаст токен вида <Text code>123456789:ABCdefGHI...</Text> — скопируйте его.
                      Это секретный ключ, не публикуйте его.
                    </Text>
                  </>
                ),
              },
              {
                color: '#0088cc',
                children: (
                  <>
                    <Text strong>3. Настройте кнопку меню</Text>
                    <br />
                    <Text type="secondary">
                      В BotFather: <Text code>/mybots</Text> → выберите бота → <Text code>Bot Settings</Text> →{' '}
                      <Text code>Menu Button</Text> → <Text code>Configure menu button</Text>.
                      Вставьте URL мини-приложения (уточните у администратора платформы) и текст кнопки «Заказать еду».
                    </Text>
                  </>
                ),
              },
              {
                color: '#0088cc',
                children: (
                  <>
                    <Text strong>4. Привяжите бота</Text>
                    <br />
                    <Text type="secondary">
                      Передайте администратору платформы: <Text strong>токен бота</Text> и{' '}
                      <Text strong>username бота</Text>. Они привяжут бота к вашему аккаунту.
                    </Text>
                  </>
                ),
              },
              {
                color: 'green',
                dot: <CheckCircleOutlined />,
                children: (
                  <>
                    <Text strong>5. Проверьте</Text>
                    <br />
                    <Text type="secondary">
                      Откройте бота → нажмите кнопку меню → убедитесь, что каталог загружается
                      и можно оформить заказ.
                    </Text>
                  </>
                ),
              },
            ]}
          />
          <Alert
            message="Важно"
            description="Username бота нельзя изменить после создания — выбирайте осмысленное и запоминающееся имя."
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
          />
        </>
      ),
    },
    {
      title: 'Подключите ВКонтакте',
      icon: <MessageOutlined />,
      color: '#4680C2',
      description: 'Создайте VK Mini App и пройдите модерацию',
      content: (
        <>
          <Paragraph>
            VK мини-приложение откроет доступ к аудитории ВКонтакте —
            клиенты смогут заказывать еду не выходя из соцсети.
          </Paragraph>
          <Timeline
            items={[
              {
                color: '#4680C2',
                children: (
                  <>
                    <Text strong>1. Создайте приложение</Text>
                    <br />
                    <Text type="secondary">
                      Перейдите на <Text code>vk.com/apps?act=manage</Text> → нажмите «Создать» →
                      выберите тип «Mini App» → введите название → выберите категорию «Еда и напитки».
                    </Text>
                  </>
                ),
              },
              {
                color: '#4680C2',
                children: (
                  <>
                    <Text strong>2. Укажите URL приложения</Text>
                    <br />
                    <Text type="secondary">
                      В настройках приложения вставьте URL — его предоставит администратор платформы.
                    </Text>
                  </>
                ),
              },
              {
                color: '#4680C2',
                children: (
                  <>
                    <Text strong>3. Привяжите к аккаунту</Text>
                    <br />
                    <Text type="secondary">
                      Скопируйте ID приложения (число в URL страницы настроек) и передайте
                      администратору платформы вместе с защищённым ключом.
                    </Text>
                  </>
                ),
              },
              {
                color: '#4680C2',
                children: (
                  <>
                    <Text strong>4. Подготовьте материалы для модерации</Text>
                    <br />
                    <Text type="secondary">Для прохождения модерации VK подготовьте:</Text>
                    <ul style={{ margin: '4px 0 0 16px', color: 'rgba(0,0,0,0.45)' }}>
                      <li>Иконка 576×576 px (PNG, без скруглений)</li>
                      <li>Иконка 150×150 px</li>
                      <li>Скриншот работающего приложения (от 1080px)</li>
                      <li>Краткое описание (до 140 символов)</li>
                      <li>Полное описание (до 4000 символов)</li>
                      <li>Описание 2-3 пользовательских сценариев</li>
                    </ul>
                  </>
                ),
              },
              {
                color: '#4680C2',
                children: (
                  <>
                    <Text strong>5. Отправьте на модерацию</Text>
                    <br />
                    <Text type="secondary">
                      Загрузите все материалы и отправьте приложение на модерацию.
                      Пока ждёте — приложение уже доступно вам для тестирования.
                    </Text>
                  </>
                ),
              },
              {
                color: 'green',
                dot: <CheckCircleOutlined />,
                children: (
                  <>
                    <Text strong>6. Готово!</Text>
                    <br />
                    <Text type="secondary">
                      После одобрения модерацией (1-5 рабочих дней) приложение станет доступно
                      всем пользователям ВКонтакте.
                    </Text>
                  </>
                ),
              },
            ]}
          />
          <Alert
            message="Совет"
            description="Используйте качественные фото блюд на скриншотах — это повышает шансы пройти модерацию с первого раза."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </>
      ),
    },
    {
      title: 'Подключите CRM',
      icon: <ApiOutlined />,
      color: '#722ed1',
      description: 'Интеграция с Frontpad или другой CRM',
      content: (
        <>
          <Paragraph>
            Если у вас уже есть CRM-система для учёта заказов (Frontpad, iiko, r_keeper и др.),
            подключите её для автоматической передачи заказов. Это необязательный шаг —
            вы можете работать и без CRM, управляя заказами прямо из админ-панели.
          </Paragraph>
          <Card size="small" style={{ background: '#f9f0ff', border: '1px solid #d3adf7' }}>
            <Title level={5} style={{ margin: '0 0 12px' }}>Настройка CRM-интеграции</Title>
            <Timeline
              items={[
                {
                  color: '#722ed1',
                  children: (
                    <>
                      <Text strong>Перейдите в Настройки → CRM-интеграция</Text>
                      <br />
                      <Text type="secondary">
                        В боковом меню выберите Настройки, затем CRM-интеграция.
                      </Text>
                    </>
                  ),
                },
                {
                  color: '#722ed1',
                  children: (
                    <>
                      <Text strong>Выберите вашу CRM-систему</Text>
                      <br />
                      <Text type="secondary">
                        Поддерживается Frontpad. Выберите его из списка
                        и введите API-ключ (secret) из личного кабинета Frontpad.
                      </Text>
                    </>
                  ),
                },
                {
                  color: '#722ed1',
                  children: (
                    <>
                      <Text strong>Настройте соответствие товаров</Text>
                      <br />
                      <Text type="secondary">
                        Укажите Frontpad-артикулы для каждого товара
                        в карточках товаров в поле Артикул CRM.
                      </Text>
                    </>
                  ),
                },
                {
                  color: '#722ed1',
                  children: (
                    <>
                      <Text strong>Проверьте связь</Text>
                      <br />
                      <Text type="secondary">
                        Нажмите «Тест подключения» — система проверит API-ключ.
                      </Text>
                    </>
                  ),
                },
                {
                  color: '#722ed1',
                  children: (
                    <>
                      <Text strong>Сделайте тестовый заказ</Text>
                      <br />
                      <Text type="secondary">
                        Оформите заказ и проверьте, что он появился в CRM.
                      </Text>
                    </>
                  ),
                },
              ]}
            />
          </Card>
          <Alert
            message="Подсказка"
            description="CRM-интеграция — необязательный шаг. Подключить CRM можно в любое время."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </>
      ),
    },
    {
      title: 'Проверьте и запускайте',
      icon: <CheckCircleOutlined />,
      color: '#52c41a',
      description: 'Финальный чек-лист перед запуском',
      content: (
        <>
          <Paragraph>
            Перед объявлением клиентам о запуске убедитесь, что всё работает.
            Сделайте тестовый заказ на каждом канале.
          </Paragraph>
          <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Title level={5} style={{ margin: '0 0 12px' }}>✅ Финальный чек-лист</Title>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text>☐ Общие настройки заполнены</Text>
                <br />
                <Text>☐ Филиал — адрес указан</Text>
                <br />
                <Text>☐ Зоны доставки настроены</Text>
                <br />
                <Text>☐ Категории созданы</Text>
                <br />
                <Text>☐ Товары добавлены (10+ шт)</Text>
              </Col>
              <Col span={12}>
                <Text>☐ Способы оплаты включены</Text>
                <br />
                <Text>☐ Сайт — каталог загружается</Text>
                <br />
                <Text>☐ TG-бот — заказ проходит</Text>
                <br />
                <Text>☐ VK-приложение — работает</Text>
                <br />
                <Text>☐ Тестовый заказ виден в Заказах</Text>
                <br />
                <Text>☐ CRM подключена (если есть)</Text>
                <br />
                <Text>☐ CRM подключена (если есть)</Text>
              </Col>
            </Row>
          </Card>
          <Alert
            message="Готовы к запуску!"
            description="Попросите друга или коллегу сделать заказ — свежий взгляд поможет найти проблемы. Проверьте каталог на мобильном телефоне."
            type="success"
            showIcon
            icon={<RocketOutlined />}
            style={{ marginTop: 16 }}
          />
        </>
      ),
    },
  ];


  return (
    <Card
      id="quick-start-overview"
      style={{
        marginBottom: 32,
        borderRadius: 12,
        border: '2px solid #ff8c00',
        boxShadow: '0 4px 16px rgba(255, 140, 0, 0.1)',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <RocketOutlined style={{ fontSize: 40, color: '#ff8c00' }} />
        <Title level={3} style={{ margin: '8px 0 4px' }}>
          Быстрый старт — запуск сервиса с нуля
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 15, maxWidth: 600, margin: '0 auto' }}>
          Пошаговая инструкция: от настройки аккаунта до приёма первых заказов через сайт,
          Telegram, ВКонтакте и CRM
        </Paragraph>
      </div>


      <Steps
        current={currentStep}
        onChange={setCurrentStep}
        direction="horizontal"
        size="small"
        style={{ marginBottom: 24 }}
        items={steps.map((step) => ({
          title: step.title,
          icon: step.icon,
          description: step.description,
        }))}
      />


      <Card
        style={{
          background: '#fafafa',
          borderRadius: 8,
          border: `1px solid ${steps[currentStep].color}22`,
        }}
      >
        <div
          id={
            currentStep === 3
              ? 'quick-start-telegram'
              : currentStep === 4
                ? 'quick-start-vk'
                : currentStep === 5
                  ? 'quick-start-first-order'
                  : undefined
          }
        >
          {steps[currentStep].content}
        </div>
      </Card>
    </Card>
  );
};


const HelpCenterPage: React.FC = () => {
  const location = useLocation();


  // Прокрутка к якорю при переходе по ссылке #section-id
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [location.hash]);


  // Элементы для Anchor-навигации
  const anchorItems = [
    {
      key: 'cat-quickstart',
      href: '#quick-start-overview',
      title: 'Быстрый старт',
      children: quickStartItems.map((item) => ({
        key: item.id,
        href: `#${item.id}`,
        title: item.title,
      })),
    },
    ...helpCategories.map((cat) => ({
      key: `cat-${cat.title}`,
      href: `#${cat.items[0]?.id || ''}`,
      title: cat.title,
      children: cat.items.map((item) => ({
        key: item.id,
        href: `#${item.id}`,
        title: item.title,
      })),
    })),
  ];


  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Обучение — как работать с сервисом</Title>
      <Paragraph type="secondary" style={{ fontSize: 15, maxWidth: 800, marginBottom: 32 }}>
        Добро пожаловать в справочный центр! Здесь описаны все разделы админ-панели, их возможности
        и полезные советы по использованию. Используйте навигацию справа для быстрого перехода
        к нужному разделу. Разделы, отмеченные тегом плана, доступны при подключении
        соответствующей подписки.
      </Paragraph>


      <Row gutter={24}>
        {/* Основной контент */}
        <Col xs={24} lg={18}>
          {/* Блок Быстрый старт */}
          <QuickStartBlock />


          {helpCategories.map((cat) => (
            <div key={cat.title} style={{ marginBottom: 32 }}>
              <Divider orientation="left" style={{ fontSize: 18 }}>
                <Space>
                  {categoryIcons[cat.title]}
                  {cat.title}
                </Space>
              </Divider>


              <Collapse
                defaultActiveKey={[]}
                expandIconPosition="start"
                style={{ background: 'transparent', border: 'none' }}
                items={cat.items.map((item) => ({
                  key: item.id,
                  label: (
                    <Space>
                      <Text strong>{item.title}</Text>
                      {item.planRequired && (
                        <Tag color={planColors[item.planRequired] || 'default'} style={{ marginLeft: 4 }}>
                          {planLabels[item.planRequired] || item.planRequired}
                        </Tag>
                      )}
                    </Space>
                  ),
                  children: <HelpSectionCard item={item} />,
                }))}
              />
            </div>
          ))}
        </Col>


        {/* Боковая навигация */}
        <Col xs={0} lg={6}>
          <Anchor
            offsetTop={80}
            items={anchorItems}
            style={{
              position: 'sticky',
              top: 80,
            }}
          />
        </Col>
      </Row>
    </div>
  );
};


export { HelpCenterPage };
